import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import {
  clearReloadAttempt,
  fetchLatestVersion,
  reloadIfUpdateAvailable,
  RUNNING_VERSION,
} from '@/utils/appVersionCheck';

const VERSION_POLL_MS = 10 * 60 * 1000;

export function useAppVersionCheck() {
  const location = useLocation();
  const [updateAvailable, setUpdateAvailable] = useState(false);
  const [latestVersion, setLatestVersion] = useState(null);
  const inFlightRef = useRef(false);
  const initialCheckDoneRef = useRef(false);

  const runCheck = useCallback(async ({ allowReload = true } = {}) => {
    if (process.env.NODE_ENV === 'development' || !RUNNING_VERSION) {
      return;
    }
    if (inFlightRef.current) return;
    inFlightRef.current = true;

    try {
      const result = await fetchLatestVersion();
      if (!result.ok) return;

      if (!result.updateAvailable) {
        clearReloadAttempt();
        setLatestVersion(null);
        setUpdateAvailable(false);
        return;
      }

      setLatestVersion(result.latestVersion);
      const reloaded = allowReload && (await reloadIfUpdateAvailable(result.latestVersion));
      if (!reloaded) {
        setUpdateAvailable(true);
      }
    } finally {
      inFlightRef.current = false;
    }
  }, []);

  // In-app navigation (opening panels, tabs, query params) must never hard-reload
  // the page mid-task; it only surfaces the update banner.
  useEffect(() => {
    const allowReload = !initialCheckDoneRef.current;
    initialCheckDoneRef.current = true;
    void runCheck({ allowReload });
  }, [location.pathname, location.search, runCheck]);

  useEffect(() => {
    if (process.env.NODE_ENV === 'development' || !RUNNING_VERSION) {
      return undefined;
    }

    const onVisible = () => {
      if (document.visibilityState === 'visible') {
        void runCheck();
      }
    };

    document.addEventListener('visibilitychange', onVisible);
    const intervalId = window.setInterval(() => {
      void runCheck();
    }, VERSION_POLL_MS);

    return () => {
      document.removeEventListener('visibilitychange', onVisible);
      window.clearInterval(intervalId);
    };
  }, [runCheck]);

  return {
    updateAvailable,
    latestVersion,
    runningVersion: RUNNING_VERSION,
    checkNow: runCheck,
  };
}
